// Practice Details — the "Charge templates" card: the practice's coding rules as
// data (C's charge_templates), each one testable against a real visit before it is
// trusted. Mounted with one line:
//
//   {!readOnly && <ChargeTemplatesCard practiceId={id} isAdmin={isAdmin} />}
//
// Gates itself on `features.charge_templates`. Admins edit and turn templates on
// and off; everyone else reads and tests.
//
// What this card deliberately does NOT do: apply a seed. A preset (Vortex,
// Valetudo) goes onto a practice only through `scripts/seed_charge_templates.py
// --apply --confirm`, which inserts templates INACTIVE — applying one to a real
// practice is David's decision (sprint stop condition). What lands here is a list
// with inactive templates marked for review, and turning one on is its own confirm.
//
// A template changes future DRAFTS only; it never edits a saved claim or submits
// anything (C's charge_capture_service).

import React, { useCallback, useEffect, useRef, useState } from "react";
import useCapabilities from "../../hooks/useCapabilities";
import TemplateEditor from "./TemplateEditor";
import TemplateTest from "./TemplateTest";
import {
  SEED_PRESETS,
  fetchSeedPreview,
  fetchTemplates,
  lineLabel,
  updateTemplate,
} from "./api";
import "./Charges.css";

function matchLabel(match) {
  const entries = Object.entries(match || {});
  if (entries.length === 0) return "every visit";
  return entries
    .map(([key, value]) =>
      key === "any"
        ? "any of several groups"
        : `${key.replace(/_/g, " ")} ${Array.isArray(value) ? value.join(" / ") : String(value)}`,
    )
    .join("; ");
}

export default function ChargeTemplatesCard({ practiceId, isAdmin = false }) {
  const { features } = useCapabilities(practiceId);
  const shown = !!practiceId && !!features.charge_templates;

  const [templates, setTemplates] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [editing, setEditing] = useState(null); // template, or {} for new
  const [testing, setTesting] = useState(null); // template
  const [toggling, setToggling] = useState(null); // template awaiting confirm
  const [toggleBusy, setToggleBusy] = useState(false);
  const [seedPreset, setSeedPreset] = useState("");
  const [seed, setSeed] = useState(null);
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedError, setSeedError] = useState(null);
  const toggleInFlight = useRef(false);

  const load = useCallback(async () => {
    if (!shown) return;
    setError(null);
    try {
      setTemplates(await fetchTemplates(practiceId));
    } catch (err) {
      setError(err.message);
    }
  }, [shown, practiceId]);

  useEffect(() => {
    setTemplates(null);
    // The reading is per practice: "already here — would be skipped" is computed
    // against THIS practice's templates, and nothing in the table names a practice.
    // Leaving it up after a switch is a wrong answer to the question David uses it
    // for (whether to run the seed script on that practice).
    setSeed(null);
    setSeedPreset("");
    setSeedError(null);
    load();
  }, [load]);

  if (!shown) return null;

  const toggle = async () => {
    if (!toggling || toggleInFlight.current) return;
    toggleInFlight.current = true;
    setToggleBusy(true);
    const turningOn = !toggling.active;
    try {
      await updateTemplate(practiceId, toggling.id, {
        active: turningOn,
        version: toggling.version,
      });
      setMessage({
        type: "success",
        text: `${toggling.name} is ${turningOn ? "on: new drafts will use it" : "off: new drafts will not use it"}.`,
      });
      setToggling(null);
      load();
    } catch (err) {
      setMessage({
        type: "error",
        text:
          err.status === 409
            ? "Someone changed this template since the list loaded. Reload and check it again."
            : err.message,
      });
      setToggling(null);
      if (err.status === 409) load();
    } finally {
      toggleInFlight.current = false;
      setToggleBusy(false);
    }
  };

  const inactiveCount = templates
    ? templates.filter((t) => !t.active).length
    : 0;

  return (
    <div className="integrations-section chg-templates-card">
      <div className="chg-card-head">
        <h3 className="practice-rules-section-title">Charge templates</h3>
        {isAdmin && templates && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setEditing({})}
          >
            New template
          </button>
        )}
      </div>
      <p className="chg-muted chg-card-lede">
        How a visit becomes a draft claim: the lowest-priority active template
        whose conditions all hold sets the lines, modifiers, POS, price and who
        is billed. Templates change new drafts only; they never edit or submit a
        claim.
      </p>

      {message && (
        <div className={`chg-message ${message.type}`} role="status">
          <span>{message.text}</span>
          <button
            className="chg-message-close"
            aria-label="Dismiss"
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </div>
      )}
      {error && (
        <div className="chg-message error" role="alert">
          {error}
        </div>
      )}
      {!error && !templates && <p className="chg-muted">Loading…</p>}

      {templates && templates.length === 0 && (
        <p className="chg-muted">
          No templates yet, so capture drafts behave exactly as before. A preset
          for this practice is applied by script after review, not from here.
        </p>
      )}

      {templates && inactiveCount > 0 && (
        <div className="chg-message warn">
          {inactiveCount} template{inactiveCount === 1 ? " is" : "s are"} not
          on. Test each against a visit before turning it on.
        </div>
      )}

      {templates && templates.length > 0 && (
        <div className="chg-table-wrap">
          <table className="chg-table">
            <thead>
              <tr>
                <th className="num">Priority</th>
                <th>Template</th>
                <th>Matches</th>
                <th>Lines</th>
                <th>Bill to</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id} className={t.active ? "" : "chg-row-inactive"}>
                  <td className="num">{t.priority ?? "—"}</td>
                  <td>
                    <strong>{t.name}</strong>
                    <div className="chg-facts">
                      <span
                        className={`chg-chip ${t.active ? "good" : "warn"}`}
                      >
                        {t.active ? "On" : "Not on — review"}
                      </span>
                      {t.doNotBill && (
                        <span className="chg-chip bad">Do not bill</span>
                      )}
                      {t.requiresNote && (
                        <span className="chg-chip muted">Needs a note</span>
                      )}
                      <span className="chg-muted">v{t.version}</span>
                    </div>
                  </td>
                  <td className="chg-match">{matchLabel(t.match)}</td>
                  <td>
                    {t.doNotBill
                      ? "—"
                      : t.lines.map((l, i) => (
                          <div key={i}>{lineLabel(l)}</div>
                        ))}
                    {Object.keys(t.payerOverrides).length > 0 && (
                      <div className="chg-muted">
                        Payer rules: {Object.keys(t.payerOverrides).join(", ")}
                      </div>
                    )}
                  </td>
                  <td>
                    {t.doNotBill
                      ? "—"
                      : t.billTo === "patient"
                        ? "Patient"
                        : "Insurance"}
                  </td>
                  <td className="chg-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setTesting(t)}
                    >
                      Test…
                    </button>
                    {isAdmin && (
                      <>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setEditing(t)}
                        >
                          Edit…
                        </button>
                        <button
                          className={`btn btn-sm ${t.active ? "btn-secondary" : "btn-primary"}`}
                          onClick={() => setToggling(t)}
                        >
                          {t.active ? "Turn off…" : "Turn on…"}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && (
        <div className="chg-seed">
          <h4>Presets</h4>
          <p className="chg-muted">
            What a preset would add, for review. Nothing here writes: applying one
            is <code>scripts/seed_charge_templates.py --apply --confirm</code>, run
            by a person, and it inserts templates switched off for you to turn on
            here one at a time.
          </p>
          <div className="chg-test-pick">
            <label className="chg-field">
              <span>Preset</span>
              <select
                value={seedPreset}
                onChange={(e) => {
                  setSeedPreset(e.target.value);
                  setSeed(null);
                  setSeedError(null);
                }}
              >
                <option value="">Choose a preset…</option>
                {SEED_PRESETS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="btn btn-secondary btn-sm"
              disabled={!seedPreset || seedBusy}
              onClick={async () => {
                setSeedBusy(true);
                setSeedError(null);
                try {
                  setSeed(await fetchSeedPreview(practiceId, seedPreset));
                } catch (err) {
                  setSeed(null);
                  setSeedError(err.message);
                } finally {
                  setSeedBusy(false);
                }
              }}
            >
              {seedBusy ? "Reading…" : "Read the preset"}
            </button>
          </div>

          {seedError && (
            <div className="chg-message error" role="alert">
              {seedError}
            </div>
          )}

          {seed && (
            <div className="chg-seed-result" data-testid="seed-preview">
              <p className="chg-muted">
                {seed.templates.length} template
                {seed.templates.length === 1 ? "" : "s"} in this preset
                {seed.skippedExisting.length > 0
                  ? `; ${seed.skippedExisting.length} the practice already has would be skipped`
                  : ""}
                . Nothing has been written.
              </p>
              <table className="chg-table">
                <thead>
                  <tr>
                    <th className="num">Priority</th>
                    <th>Template</th>
                    <th>Matches</th>
                    <th>Lines</th>
                    <th>Bill to</th>
                  </tr>
                </thead>
                <tbody>
                  {seed.templates.map((t, i) => {
                    const already = seed.skippedExisting.includes(t.name);
                    return (
                      <tr key={`${t.name}-${i}`} className="chg-row-inactive">
                        <td className="num">{t.priority ?? "—"}</td>
                        <td>
                          <strong>{t.name}</strong>
                          <div className="chg-facts">
                            {already && (
                              <span className="chg-chip muted">
                                already here — would be skipped
                              </span>
                            )}
                            {t.doNotBill && (
                              <span className="chg-chip bad">Do not bill</span>
                            )}
                            {t.requiresNote && (
                              <span className="chg-chip muted">Needs a note</span>
                            )}
                          </div>
                        </td>
                        <td className="chg-match">{matchLabel(t.match)}</td>
                        <td>
                          {t.doNotBill
                            ? "—"
                            : t.lines.map((l, j) => (
                                <div key={j}>{lineLabel(l)}</div>
                              ))}
                        </td>
                        <td>
                          {t.doNotBill
                            ? "—"
                            : t.billTo === "patient"
                              ? "Patient"
                              : "Insurance"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {toggling && (
        <div
          className="chg-modal-backdrop"
          onClick={() => !toggleBusy && setToggling(null)}
        >
          <div
            className="chg-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="chg-toggle-title"
          >
            <div className="chg-modal-header">
              <h2 id="chg-toggle-title">
                {toggling.active ? "Turn off" : "Turn on"} {toggling.name}
              </h2>
            </div>
            <div className="chg-modal-body">
              <p>
                {toggling.active
                  ? "New capture drafts will stop using this template. Drafts already made keep their lines."
                  : `New capture drafts for visits matching "${matchLabel(toggling.match)}" will be built from this template. Existing claims are not changed.`}
              </p>
            </div>
            <div className="chg-modal-footer">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setToggling(null)}
                disabled={toggleBusy}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={toggle}
                disabled={toggleBusy}
              >
                {toggleBusy
                  ? "Saving…"
                  : toggling.active
                    ? "Turn off"
                    : "Turn on"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <TemplateEditor
          practiceId={practiceId}
          template={editing.id != null ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setMessage({
              type: "success",
              text: `${saved.name} saved (v${saved.version}).`,
            });
            load();
          }}
        />
      )}

      {testing && (
        <TemplateTest
          practiceId={practiceId}
          template={testing}
          onClose={() => setTesting(null)}
        />
      )}
    </div>
  );
}
